/**
 * @file MineTeamSwitch.tsx
 * @brief The admin's "Mine ▾" / "Team ▾" menu button and a team row's owner
 *        line.
 *
 * @details
 * It renders nothing for anyone who is not a company admin
 * (`useMeetingListScope`). Same storage as before.
 *
 * U1 (DES 29-10-2026, mockup scratch/dispatcher/mine-team-popover-mockup.html):
 * was a two-segment Mine | Team pill, taller than the heading line it sat
 * beside. Now one small button with the current choice and a ▾ that opens a
 * menu: "Mine · Your own meetings", "Team · Everyone in your company", the
 * current one ticked in brand red. Radix gives the menu role, menuitemradio
 * items, arrow keys, Esc and click-outside. The button is 28px, inside the
 * heading's 36px chevron line, so it never makes the heading taller. Same
 * words as the phone (FRONTEND MineTeamSwitch.tsx).
 *
 * @author DES
 * @date 22-10-2026
 */

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { PersonPhoto } from "@/components/ui/PersonPhoto";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import {
  ownerMeetingLabel,
  useMeetingListScope,
  type MeetingListScope,
} from "../meetingListScope";

const MINE_TEAM_OPTIONS: { id: MeetingListScope; label: string; help: string }[] = [
  { id: "mine", label: "Mine", help: "Your own meetings" },
  { id: "team", label: "Team", help: "Everyone in your company" },
];

export function MineTeamSwitch({ className }: { className?: string }) {
  const { available, scope, setScope } = useMeetingListScope();
  if (!available) return null;
  const current = MINE_TEAM_OPTIONS.find((o) => o.id === scope) ?? MINE_TEAM_OPTIONS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Show: ${current.label}`}
          className={cn(
            "inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-black/[0.05] pl-3 pr-2 text-[13px] font-semibold text-app-text transition-colors hover:bg-black/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 data-[state=open]:bg-black/[0.08] dark:bg-white/[0.07] dark:hover:bg-white/[0.11] dark:data-[state=open]:bg-white/[0.11]",
            className,
          )}
          // Inside a collapsible heading: opening must not fold the section.
          onClick={(e) => e.stopPropagation()}
        >
          {current.label}
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.4} aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-[220px] rounded-[14px] p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuPrimitive.RadioGroup
          value={current.id}
          onValueChange={(v) => setScope(v === "team" ? "team" : "mine")}
        >
          {MINE_TEAM_OPTIONS.map((o) => (
            <DropdownMenuPrimitive.RadioItem
              key={o.id}
              value={o.id}
              className="flex cursor-default select-none items-center gap-2.5 rounded-[9px] px-2.5 py-2 outline-none transition-colors focus:bg-black/[0.05] data-[state=checked]:bg-black/[0.04] dark:focus:bg-white/[0.07] dark:data-[state=checked]:bg-white/[0.06]"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-app-text">{o.label}</span>
                <span className="block text-xs text-app-text-secondary">{o.help}</span>
              </span>
              <DropdownMenuPrimitive.ItemIndicator>
                <Check className="h-4 w-4 text-brand" strokeWidth={2.4} />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuPrimitive.RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Ali's meeting" with Ali's photo, on a row someone else owns. */
export function MeetingOwnerLine({
  owner,
  className,
}: {
  owner: { firstName?: string | null; name?: string | null; avatarUrl?: string | null } | null | undefined;
  className?: string;
}) {
  if (!owner) return null;
  const label = ownerMeetingLabel(owner);
  return (
    <span
      title={owner.name?.trim() || undefined}
      className={cn("flex min-w-0 items-center gap-1.5", className)}
    >
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-app-quaternary text-[8px] font-bold text-app-text">
        <PersonPhoto
          src={owner.avatarUrl ?? null}
          fallback={getInitials(owner.name?.trim() || owner.firstName?.trim() || "?")}
        />
      </span>
      <span className="min-w-0 truncate text-[13px] font-semibold text-app-text-secondary">
        {label}
      </span>
    </span>
  );
}
